<script lang="ts" setup>
import FloatingColorPicker from '#components/floating-color-picker/FloatingColorPicker.vue'
import AntDesignCloseOutlined from '#components/icons/AntDesignCloseOutlined.vue'
import IonInformationCircle from '#components/icons/IonInformationCircle.vue'
import IonMdWarning from '#components/icons/IonMdWarning.vue'
import LineMdLoadingTwotoneLoop from '#components/icons/LineMdLoadingTwotoneLoop.vue'
import {
  $isCustomizationActive,
  $isPrintSimulationLoaded,
  $isPrintSimulationLoading,
  $isPrintSimulationMatchPreview,
  $outputLayers,
  $previewWatermarkHelpText,
  $printSimulationError,
  $printSimulationErrorText,
  $printSimulationHelpText,
  $printSimulationWarnText,
  renderPrintOutputSimulation,
  type Layer
} from '#components/product-customization-board'
import { useStore } from '@nanostores/vue'
import { Tooltip } from 'floating-vue'
import { cloneDeep, debounce } from 'lodash'
import { computed } from 'vue'

const isCustomizationActive = useStore($isCustomizationActive)
const previewWatermarkHelpText = useStore($previewWatermarkHelpText)
const printSimulationHelpText = useStore($printSimulationHelpText)
const printSimulationError = useStore($printSimulationError)
const isPrintSimulationLoading = useStore($isPrintSimulationLoading)
const isPrintSimulationLoaded = useStore($isPrintSimulationLoaded)
const isPrintSimulationMatchPreview = useStore($isPrintSimulationMatchPreview)

const deactivateCustomization = () => {
  $isCustomizationActive.set(false)
}

const outputLayers = useStore($outputLayers)

const gridResponsiveColsClass = computed(() => {
  const layersCount = outputLayers.value.length

  if (layersCount === 1) return 'tw-grid-cols-1'
  if (layersCount === 2 || layersCount === 4) return 'tw-grid-cols-1 md:tw-grid-cols-2'

  return 'tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3'
})

const issueText = computed(() => {
  if (isPrintSimulationMatchPreview.value) return ''
  if (isPrintSimulationLoaded.value && !isPrintSimulationMatchPreview.value) return $printSimulationWarnText.get()

  return printSimulationError.value ? $printSimulationErrorText.get() : ''
})

const onLayerColorChange = debounce((layer: (typeof outputLayers.value)[0], index: number, color: string) => {
  const newLayer = cloneDeep(layer as Layer)

  newLayer.color = color

  const newLayers = cloneDeep(outputLayers.value as Layer[])

  newLayers[index] = newLayer
  $outputLayers.set(newLayers)
}, 250)
</script>

<template>
  <section
    id="productCustomizationForm"
    aria-labelledby="productCustomizationToggler"
    class="product-customization-form"
    :class="
      isCustomizationActive
        ? '[box-shadow:0px_-10px_20px_rgba(0,0,0,0.2)] md:tw-translate-y-0 md:tw-shadow-lg'
        : 'tw-pointer-events-none tw-translate-y-[105%] md:-tw-translate-y-full'
    "
  >
    <div class="tw-flex tw-flex-col tw-gap-4 tw-p-4 lg:tw-gap-6 lg:tw-px-6 lg:tw-py-4">
      <div class="tw-flex tw-flex-col tw-flex-wrap tw-gap-3 lg:tw-flex-row lg:tw-gap-6">
        <div
          v-if="previewWatermarkHelpText"
          class="tw-flex tw-min-w-[20ch] tw-flex-1 tw-items-center tw-gap-3 tw-text-xs lg:tw-text-sm"
        >
          <IonInformationCircle class="tw-h-4 tw-w-4 tw-min-w-4 lg:tw-h-6 lg:tw-w-6 lg:tw-min-w-6" />
          {{ previewWatermarkHelpText }}
        </div>

        <div class="product-customization-form__cta-group">
          <Tooltip
            :triggers="['click', 'hover', 'touch', 'focus']"
            class="tw-flex tw-items-center"
            distance="10"
            :disabled="!printSimulationHelpText"
          >
            <button
              type="button"
              :disabled="isPrintSimulationLoading"
              class="product-customization-form__cta button button--secondary tw-mr-auto tw-gap-2"
              @click="renderPrintOutputSimulation"
            >
              <LineMdLoadingTwotoneLoop v-if="isPrintSimulationLoading" class="tw-h-5 tw-w-5 md:tw-h-6 md:tw-w-6" />
              <span>{{ isPrintSimulationLoading ? 'Simulating Print' : 'Check Print Simulation' }}</span>
            </button>

            <template #popper>
              <div class="tw-w-56 tw-text-sm md:tw-w-72">
                {{ printSimulationHelpText }}
              </div>
            </template>
          </Tooltip>
          <button
            class="product-customization-form__cta product-customization-form__cta--close button button--primary"
            @click="deactivateCustomization"
          >
            <AntDesignCloseOutlined class="tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6" />
          </button>
        </div>
      </div>

      <div
        v-if="issueText"
        class="tw-flex tw-flex-1 tw-items-center tw-gap-3 tw-text-xs lg:tw-text-sm"
        :class="printSimulationError ? 'tw-text-red-700' : 'tw-text-amber-600'"
      >
        <IonMdWarning class="tw-h-4 tw-w-4 tw-min-w-4 lg:tw-h-6 lg:tw-w-6 lg:tw-min-w-6" />
        {{ issueText }}
      </div>
    </div>

    <div class="tw-grid tw-overflow-hidden tw-bg-white" :class="gridResponsiveColsClass">
      <div
        v-for="(layer, index) in outputLayers"
        :key="layer.fieldId"
        class="tw-flex tw-min-h-0 tw-min-w-0 tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-gray-100 tw-px-4 tw-py-2 tw-transition-all [border-top-style:solid] lg:tw-px-6"
      >
        <div class="tw-text-xs lg:tw-text-sm">
          {{ layer.fieldLabel }}
        </div>

        <FloatingColorPicker
          :model-value="layer.color"
          @update:model-value="(color) => onLayerColorChange(layer, index, color)"
        />
      </div>
    </div>
  </section>
</template>

<style lang="postcss" scoped>
.product-customization-form {
  @apply tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-[3000000000] tw-flex tw-w-full tw-flex-col tw-border-t tw-bg-white tw-transition-all tw-duration-300 tw-ease-in-out [border-top-style:solid] md:tw-relative md:tw-z-[1] md:tw-border md:tw-border-t-0 md:tw-border-solid;
}

.product-customization-form__cta-group {
  @apply tw-flex tw-min-w-max tw-flex-1 tw-items-center tw-justify-end tw-gap-2 lg:tw-gap-4;
}

.product-customization-form__cta {
  @apply tw-h-8 tw-min-h-8 tw-min-w-fit tw-px-3 tw-text-xs tw-shadow-md md:tw-px-4 lg:tw-h-10 lg:tw-min-h-10 lg:tw-text-sm;

  &,
  &::before,
  &::after {
    @apply tw-rounded-full;
  }
}

.product-customization-form__cta--close {
  @apply tw-aspect-square tw-p-0;

  &,
  &::before,
  &::after {
    @apply tw-rounded-full;
  }
}
</style>
